// Mantenimiento M1: lectura y escritura del inventario (M50). Las reglas las aplica la base (RLS y triggers); aquí se
// traducen sus errores a mensajes claros. Archivos en el bucket privado asset-files con URL firmada de 120 s.

import { supabase } from '../../supabaseClient';
import {
  BUCKET_ACTIVOS, CAMPOS_ACTIVO, MAX_BYTES_ARCHIVO, PLAZOS_EXTINTOR, PLAZOS_NORMA, SEGUNDOS_URL_ACTIVOS, TIPOS_ARCHIVO,
  type FilaActivo, type FilaDocumento, type FilaHistorial, type PlazosExtintor, type TipoDocumento,
} from './mant-tipos';
import { cambioMasivoPermitido, conteoParaChecklist, validarPlazos, type ConteoActivos } from './mant-logica';
import type { FilaNueva } from './mant-importar';

type ErrorBase = { code?: string; message: string };

/** Mensaje claro para un error de la base (los triggers ya responden en español). */
export function mensajeError(e: ErrorBase, accion: string): string {
  if (e.code === '42501' || /row-level security|permission denied/i.test(e.message)) return `No tienes permiso para ${accion}.`;
  if (e.code === '23505' || /assets_code_key/.test(e.message)) return 'Ese código ya lo usa otro activo de la empresa.';
  if (e.code === '42P01' || /does not exist|schema cache/i.test(e.message)) return 'El inventario de activos aún no está habilitado en tu cuenta.';
  if (/violates check constraint/i.test(e.message)) return `No se pudo ${accion}: revisa los datos (algún valor no es válido).`;
  if (e.code === '23514' || e.code === 'P0001') return e.message;
  return `No se pudo ${accion}: ${e.message}`;
}
const falla = (e: ErrorBase, accion: string): never => { throw new Error(mensajeError(e, accion)); };

// ── Lectura ──
export async function cargarActivos(companyId: string): Promise<FilaActivo[]> {
  const { data, error } = await supabase.from('assets').select(CAMPOS_ACTIVO).eq('company_id', companyId).order('code');
  if (error) falla(error, 'leer el inventario');
  return (data ?? []) as unknown as FilaActivo[];
}

export async function cargarActivo(id: string): Promise<FilaActivo | null> {
  const { data, error } = await supabase.from('assets').select(CAMPOS_ACTIVO).eq('id', id).maybeSingle();
  if (error) return null;
  return (data as unknown as FilaActivo) ?? null;
}

export async function cargarDocumentos(assetId: string): Promise<FilaDocumento[]> {
  const { data, error } = await supabase.from('asset_documents')
    .select('id, asset_id, kind, title, file_path, file_name, file_mime, file_size, valid_until, created_at').eq('asset_id', assetId)
    .order('created_at', { ascending: false });
  if (error) falla(error, 'leer los documentos');
  return (data ?? []) as FilaDocumento[];
}

export async function cargarHistorial(assetId: string): Promise<FilaHistorial[]> {
  const { data, error } = await supabase.from('asset_history')
    .select('id, asset_id, event, from_value, to_value, reason, detail, actor_name, created_at').eq('asset_id', assetId)
    .order('created_at', { ascending: false });
  if (error) falla(error, 'leer el historial');
  return (data ?? []) as FilaHistorial[];
}

export interface InspeccionActivo { id: string; status: string; template_name: string; scheduled_date: string | null; performed_date: string | null; signed_at: string | null }
export async function cargarInspeccionesActivo(assetId: string): Promise<InspeccionActivo[]> {
  const { data, error } = await supabase.from('insp_inspections')
    .select('id, status, template_name, scheduled_date, performed_date, signed_at').eq('asset_id', assetId)
    .order('created_at', { ascending: false });
  if (error) return [];
  return (data ?? []) as InspeccionActivo[];
}

/** Última inspección firmada por activo (columna del tablero). */
export async function ultimasInspecciones(companyId: string): Promise<Map<string, string>> {
  const { data, error } = await supabase.from('insp_inspections').select('asset_id, performed_date')
    .eq('company_id', companyId).eq('status', 'completada').not('asset_id', 'is', null);
  const m = new Map<string, string>();
  if (error) return m;
  for (const r of (data ?? []) as { asset_id: string; performed_date: string | null }[]) {
    if (r.performed_date && (!m.has(r.asset_id) || (m.get(r.asset_id) as string) < r.performed_date)) m.set(r.asset_id, r.performed_date);
  }
  return m;
}

export interface Opcion { id: string; nombre: string }
export async function cargarOpciones(companyId: string): Promise<{ trabajadores: Opcion[]; usuarios: Opcion[]; epp: Opcion[]; sectores: Opcion[] }> {
  const [w, u, e, s] = await Promise.all([
    supabase.from('workers').select('id, full_name').eq('company_id', companyId).order('full_name'),
    supabase.from('profiles').select('id, full_name, email').eq('company_id', companyId),
    supabase.from('epp_catalog').select('id, name').eq('company_id', companyId).order('name'),
    supabase.from('emergency_plan_sectors').select('id, name').eq('company_id', companyId).order('name'),
  ]);
  return {
    trabajadores: ((w.data ?? []) as { id: string; full_name: string }[]).map(x => ({ id: x.id, nombre: x.full_name })),
    usuarios: ((u.data ?? []) as { id: string; full_name: string | null; email: string | null }[])
      .map(x => ({ id: x.id, nombre: x.full_name || x.email || 'Usuario' })).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')),
    epp: ((e.data ?? []) as { id: string; name: string }[]).map(x => ({ id: x.id, nombre: x.name })),
    sectores: ((s.data ?? []) as { id: string; name: string }[]).map(x => ({ id: x.id, nombre: x.name })),
  };
}

// ── Plazos de los extintores (asset_extinguisher_intervals) ──
const COLUMNAS_PLAZOS = PLAZOS_EXTINTOR.map(p => p.clave).join(', ');

/** Plazos de la empresa; sin fila (o sin poder leerla) valen los de la norma. propios = la empresa guardó los suyos. */
export async function cargarPlazosExtintor(companyId: string): Promise<{ plazos: PlazosExtintor; propios: boolean }> {
  try {
    const { data, error } = await supabase.from('asset_extinguisher_intervals').select(COLUMNAS_PLAZOS).eq('company_id', companyId).maybeSingle();
    if (error || !data) return { plazos: { ...PLAZOS_NORMA }, propios: false };
    const fila = data as unknown as Record<string, unknown>;
    return { plazos: Object.fromEntries(PLAZOS_EXTINTOR.map(p => [p.clave, Number(fila[p.clave]) || p.norma])) as PlazosExtintor, propios: true };
  } catch {
    return { plazos: { ...PLAZOS_NORMA }, propios: false };
  }
}

export async function guardarPlazosExtintor(companyId: string, plazos: PlazosExtintor): Promise<void> {
  const err = validarPlazos(plazos);
  if (err) throw new Error(err);
  const { error } = await supabase.from('asset_extinguisher_intervals').upsert({ company_id: companyId, ...plazos }, { onConflict: 'company_id' });
  if (error) falla(error, 'guardar los plazos de los extintores');
}

/** Vuelve a los plazos de la norma (borra la fila de la empresa). */
export async function restablecerPlazosExtintor(companyId: string): Promise<void> {
  const { error } = await supabase.from('asset_extinguisher_intervals').delete().eq('company_id', companyId);
  if (error) falla(error, 'restablecer los plazos de los extintores');
}

// ── Escritura ──
export type CambiosActivo = Partial<Omit<FilaActivo, 'id' | 'company_id' | 'created_at' | 'updated_at'>> & { status_reason?: string | null };

export async function crearActivo(companyId: string, datos: CambiosActivo): Promise<FilaActivo> {
  const { data, error } = await supabase.from('assets').insert({ ...datos, company_id: companyId, code: datos.code ?? '' })
    .select(CAMPOS_ACTIVO).single();
  if (error) falla(error, 'registrar el activo');
  return data as unknown as FilaActivo;
}

export async function actualizarActivo(id: string, cambios: CambiosActivo): Promise<FilaActivo> {
  const { data, error } = await supabase.from('assets').update(cambios).eq('id', id).select(CAMPOS_ACTIVO);
  if (error) falla(error, 'guardar el activo');
  if (!data?.length) throw new Error('No tienes permiso para cambiar este activo.');
  return data[0] as unknown as FilaActivo;
}

/** Cambio de estado o aptitud de UN activo, con su motivo (la base lo exige al liberar y lo guarda en el historial). */
export async function cambiarEstado(id: string, cambio: Pick<CambiosActivo, 'operational_status' | 'safety_status'>, motivo: string): Promise<FilaActivo> {
  return actualizarActivo(id, { ...cambio, status_reason: motivo.trim() || null });
}

/** Acción masiva autorizada (responsable, ubicación, categoría o archivar). Nunca estado ni aptitud. */
export async function cambioMasivo(ids: string[], cambio: CambiosActivo): Promise<number> {
  if (!cambioMasivoPermitido(cambio)) throw new Error('Esa acción no se puede hacer en masa.');
  if (!ids.length) return 0;
  const { data, error } = await supabase.from('assets').update(cambio).in('id', ids).select('id');
  if (error) falla(error, 'aplicar la acción');
  return data?.length ?? 0;
}

export async function borrarActivo(id: string): Promise<void> {
  const { data, error } = await supabase.from('assets').delete().eq('id', id).select('id');
  if (error) falla(error, 'borrar el activo');
  if (!data?.length) throw new Error('No tienes permiso para borrar este activo.');
}

/** Importación: inserta las filas válidas de a 100. Devuelve cuántas quedaron y los errores por fila. */
export async function importarActivos(companyId: string, filas: { numero: number; datos: FilaNueva }[]): Promise<{ creados: number; errores: string[] }> {
  let creados = 0;
  const errores: string[] = [];
  for (let i = 0; i < filas.length; i += 100) {
    const lote = filas.slice(i, i + 100);
    const { data, error } = await supabase.from('assets').insert(lote.map(f => ({ ...f.datos, company_id: companyId, code: f.datos.code ?? '' }))).select('id');
    if (!error) { creados += data?.length ?? 0; continue; }
    // El lote falló: se reintenta de a una para saber qué fila tiene el problema.
    for (const f of lote) {
      const { error: e1 } = await supabase.from('assets').insert({ ...f.datos, company_id: companyId, code: f.datos.code ?? '' });
      if (e1) errores.push(`Fila ${f.numero}: ${mensajeError(e1, 'importar la fila')}`);
      else creados++;
    }
  }
  return { creados, errores };
}

// ── Archivos ──
export function validarArchivo(f: Pick<File, 'type' | 'size'>): string | null {
  if (!TIPOS_ARCHIVO.includes(f.type)) return 'Solo PDF, PNG o JPEG.';
  if (f.size <= 0) return 'El archivo está vacío.';
  if (f.size > MAX_BYTES_ARCHIVO) return 'El archivo pasa de 20 MB.';
  return null;
}

export const nombreArchivoSeguro = (nombre: string) => {
  const limpio = nombre.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9._-]+/g, '_').replace(/_+/g, '_').slice(-80);
  return limpio || 'archivo';
};

async function subir(companyId: string, assetId: string, carpeta: 'docs' | 'foto', archivo: File): Promise<string> {
  const err = validarArchivo(archivo);
  if (err) throw new Error(err);
  const ruta = `${companyId}/${assetId}/${carpeta}/${Date.now()}_${nombreArchivoSeguro(archivo.name)}`;
  const { error } = await supabase.storage.from(BUCKET_ACTIVOS).upload(ruta, archivo, { contentType: archivo.type, upsert: false });
  if (error) falla(error as ErrorBase, 'subir el archivo');
  return ruta;
}

export async function subirDocumento(companyId: string, assetId: string, kind: TipoDocumento, archivo: File, titulo: string, vence: string | null): Promise<void> {
  const ruta = await subir(companyId, assetId, 'docs', archivo);
  const { error } = await supabase.from('asset_documents').insert({
    company_id: companyId, asset_id: assetId, kind, title: titulo.trim() || null, file_path: ruta, file_name: archivo.name.slice(0, 200),
    file_mime: archivo.type, file_size: archivo.size, valid_until: vence || null,
  });
  if (error) falla(error, 'guardar el documento');
}

export async function borrarDocumento(id: string): Promise<void> {
  const { data, error } = await supabase.from('asset_documents').delete().eq('id', id).select('id');
  if (error) falla(error, 'borrar el documento');
  if (!data?.length) throw new Error('No tienes permiso para borrar este documento.');
}

export async function subirFoto(companyId: string, assetId: string, archivo: File): Promise<FilaActivo> {
  if (!archivo.type.startsWith('image/')) throw new Error('La foto debe ser PNG o JPEG.');
  const ruta = await subir(companyId, assetId, 'foto', archivo);
  return actualizarActivo(assetId, { photo_path: ruta });
}

export async function urlFirmada(ruta: string): Promise<string> {
  const { data, error } = await supabase.storage.from(BUCKET_ACTIVOS).createSignedUrl(ruta, SEGUNDOS_URL_ACTIVOS);
  if (error || !data?.signedUrl) throw new Error('No se pudo abrir el archivo. Intenta de nuevo.');
  return data.signedUrl;
}

/** Abre un archivo en otra pestaña (la pestaña se abre antes de esperar: Safari bloquea la ventana después). */
export async function abrirArchivoActivo(ruta: string): Promise<void> {
  const v = window.open('', '_blank');
  if (v) v.opener = null;
  try {
    const url = await urlFirmada(ruta);
    if (v) v.location.href = url; else window.open(url, '_blank', 'noopener,noreferrer');
  } catch (e) { v?.close(); throw e; }
}

// ── Inspección por activo ──
export interface PlantillaMin { id: string; name: string; company_id: string | null; is_library: boolean; asset_class: string | null; anexo_items: string[] }
export async function cargarPlantillasActivo(companyId: string): Promise<PlantillaMin[]> {
  const { data, error } = await supabase.from('insp_templates').select('id, name, company_id, is_library, asset_class, anexo_items, active')
    .or(`company_id.is.null,company_id.eq.${companyId}`).eq('active', true).order('name');
  if (error) return [];
  return (data ?? []) as PlantillaMin[];
}

export async function programarInspeccionActivo(p: { companyId: string; assetId: string; siteId: string; templateId: string; frecuencia: string;
  inicio: string; usuarioId: string | null }): Promise<void> {
  const unica = p.frecuencia === 'unica';
  let scheduleId: string | null = null;
  if (!unica) {
    const { data, error } = await supabase.from('insp_schedules').insert({ company_id: p.companyId, template_id: p.templateId, site_id: p.siteId,
      frequency: p.frecuencia, start_date: p.inicio, asset_id: p.assetId, created_by: p.usuarioId }).select('id').single();
    if (error) falla(error, 'programar la inspección');
    scheduleId = (data as { id: string }).id;
  }
  // La primera inspección (las siguientes de la programación las genera Inspecciones al completar cada una).
  const { error: e2 } = await supabase.from('insp_inspections').insert({ company_id: p.companyId, site_id: p.siteId, template_id: p.templateId,
    schedule_id: scheduleId, scheduled_date: p.inicio, asset_id: p.assetId, status: 'programada', created_by: p.usuarioId });
  if (e2) falla(e2, 'crear la primera inspección');
}

// ── Checklist: aplicabilidad de GT 11, GT 27 y GT 28 ──
/** Conteo de activos de ventilación y de máquinas (null si no se pudo leer: los ítems aplican, no se marca No aplica sin saber). */
export async function conteoActivosChecklist(companyId: string): Promise<ConteoActivos | null> {
  if (!companyId) return null;
  const { data, error } = await supabase.from('assets').select('category, archived_at').eq('company_id', companyId);
  if (error) return null;
  return conteoParaChecklist((data ?? []) as Pick<FilaActivo, 'category' | 'archived_at'>[]);
}
